import React, { useState, useEffect } from "react";
import { User } from 'lucide-react';
import { Row, Col, L, Input, Select, SectionTitle, FormField, FileInput} from '../../../SharedInputs.jsx';
import {COUNTRY_SACC_CODES, CountryList} from "../../../../CountrySACC.js";
import { useMemo } from 'react';
import { getAllAgents } from "../../../../services/AgentService.js";
export function SectionPersonal({ value, onChange, errors = [], dispatch }) {
  const updateField = (field, fieldValue) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const updateAgent = (field, fieldValue) => {
    onChange({
      ...value,
      agent: { ...value.agent, [field]: fieldValue }
    });
  };
  const updatePassport = (file) => {
    dispatch({
      type: 'SET_FILE',
      section: 'personal',
      field: 'passportCopy',
      file
    });
  };
  const getFieldError = (fieldName) => {
    return errors.find(error => error.toLowerCase().includes(fieldName.toLowerCase()));
  };
  const countryOptions = useMemo(() => {
    return CountryList
  }, []);
  const [agentList, setAgentList] = useState([]);
  // Fetch agents
  useEffect(() => {
    const fetchAgents = async () => {
        try {
          const data = await getAllAgents();
          const filteredAgents = data.filter(agent =>
            agent.status.toLowerCase() === "active" && !agent.agentName.toLowerCase().includes("test")
          );
          setAgentList(filteredAgents || []);
        } catch (error) {
          alert("Failed to load agents");
        }
    };
    fetchAgents();
  }, []);

  return (
    <div>
      <SectionTitle icon={User} step={1}>
        Personal Details (as shown on passport)
      </SectionTitle>

      {/* Previous Study & IDs */}
      <Row>
        <Col span={4}>
          <FormField label="Previously studied at CIET?" hint="Select 'Yes' if you have studied at CIET before">
            <Select
              value={value.previouslyStudiedAtCIET}
              onChange={(e) => updateField('previouslyStudiedAtCIET', e.target.value)}
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </Select>
          </FormField>
        </Col>
        <Col span={4}>
          <FormField 
            label="Student ID (if known)"
            hint="Only if you've studied with CIET before"
          >
            <Input
              value={value.studentId}
              onChange={(e) => updateField('studentId', e.target.value)}
              placeholder="Enter student ID"
              error={getFieldError('student id')}
            />
          </FormField>
        </Col>
        <Col span={4}>
          <FormField 
            label="USI Number (if available)"
            hint="Unique Student Identifier"
          >
            <Input
              value={value.usiNumber}
              onChange={(e) => updateField('usiNumber', e.target.value)}
              placeholder="Enter USI number"
            />
          </FormField>
        </Col>
      </Row>

      {/* Name Details */}
      <Row>
        <Col span={3}>
          <FormField label="Title"
          required
          error={getFieldError('title')}>
            <Select
              value={value.title}
              onChange={(e) => updateField('title', e.target.value)}
              required
              error={getFieldError('title')}
            >
              <option value="">Select</option>
              <option value="Mr">Mr</option>
              <option value="Ms">Ms</option>
              <option value="Miss">Miss</option>
              <option value="Mrs">Mrs</option>
              <option value="Other">Other</option>
            </Select>
          </FormField>
        </Col>
        <Col span={5}>
          <FormField 
            label="Given Names" 
            required
            error={getFieldError('given names')}
          >
            <Input
              required
              value={value.givenNames}
              onChange={(e) => updateField('givenNames', e.target.value)}
              placeholder="Enter your first and middle names"
              error={getFieldError('given names')}
            />
          </FormField>
        </Col>
        <Col span={4}>
          <FormField 
            label="Family Name" 
            required
            error={getFieldError('family name')}
          >
            <Input
              required
              value={value.familyName}
              onChange={(e) => updateField('familyName', e.target.value)}
              placeholder="Enter your surname"
              error={getFieldError('family name')}
            />
          </FormField>
        </Col>
      </Row>
      <Row>
        <Col span={6}>
          <FormField 
            label="Email Address" 
            required
            error={getFieldError('email')}
          >
            <Input
              type="email"
              required
              value={value.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="your.email@example.com"
              error={getFieldError('email')}
            />
          </FormField>
        </Col>
        <Col span={3}>
          <FormField label="Mobile Phone"
          required
          error={getFieldError('mobile phone')}>
            <Input
              type="tel"
              value={value.mobilePhone}
              onChange={(e) => updateField('mobilePhone', e.target.value)}
              placeholder="+61 4XX XXX XXX"
              required
              error={getFieldError('mobile phone')}
            />
          </FormField>
        </Col>
        <Col span={3}>
          <FormField label="Telephone">
            <Input
              type="tel"
              value={value.telephone}
              onChange={(e) => updateField('telephone', e.target.value)}
              placeholder="Home/work phone"
            />
          </FormField>
        </Col>
        
      </Row>

      {/* Personal Information */}
      <Row>
        <Col span={3}>
          <FormField 
            label="Date of Birth" 
            required
            error={getFieldError('date of birth')}
          >
            <Input
              type="date"
              required
              value={value.dateOfBirth}
              onChange={(e) => updateField('dateOfBirth', e.target.value)}
              error={getFieldError('date of birth')}
            />
          </FormField>
        </Col>
        <Col span={3}>
          <FormField 
            label="Gender" 
            required
            error={getFieldError('gender')}
          >
            <Select
              required
              value={value.gender}
              onChange={(e) => updateField('gender', e.target.value)}
              error={getFieldError('gender')}
            >
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </Select>
          </FormField>
        </Col>
        <Col span={3}>
          <FormField label="Country of Birth"
          required
          error={getFieldError('country of birth')}>
            <Select
              value={value.countryOfBirth || ''}
              onChange={(e) => updateField('countryOfBirth', e.target.value)}
              required
              error={getFieldError('countryOfBirth')}
            >
              <option value="">Select a country</option>
              {countryOptions.map((c) => (
                <option key={c.value} value={c.label}>
                  {c.label}
                </option>
              ))}
            </Select>
          </FormField>
        </Col>
        <Col span={3}>
          <FormField label="Citizenship"
          required
          error={getFieldError('citizenship')}>
            <Select
              value={value.citizenship || ''}
              onChange={(e) => updateField('citizenship', e.target.value)}
              required
              error={getFieldError('citizenship')}
            >
              <option value="">Select a country</option>
              {countryOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
           
          </FormField>
        </Col>
      </Row>

      {/* Passport Information */}
      <Row>
        <Col span={4}>
          <FormField label="Passport Number"
          required
          error={getFieldError('passport number')}>
            <Input
              value={value.passportNumber}
              onChange={(e) => updateField('passportNumber', e.target.value)}
              required
              error={getFieldError('passport number')}
              placeholder="Enter passport number"
            />
          </FormField>
        </Col>
        <Col span={4}>
          <FormField label="Passport Expiry Date"
          required
          error={getFieldError('passport expiry')}>
            <Input
              type="date"
              value={value.passportExpiry}
              onChange={(e) => updateField('passportExpiry', e.target.value)}
              required
              error={getFieldError('passport expiry')}
            />
          </FormField>
        </Col>
        <Col span={4}>
          <FormField label="Passport Country of Issue"
          required
          error={getFieldError('passport country of issue')}>
            <Select
              value={value.passportCountryOfIssue || ''}
              onChange={(e) => updateField('passportCountryOfIssue', e.target.value)}
              required
              error={getFieldError('passportCountryOfIssue')}
            >
              <option value="">Select a country</option>
              {countryOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
          </FormField>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <FormField 
            label="Upload Passport" 
            hint="Accepted formats: PDF, JPG, PNG (Max 2MB)"
            required
            error={getFieldError('passport copy')}
          >
            <FileInput
              accept=".pdf,.jpg,.jpeg,.png"
              maxSize={2 * 1024 * 1024} // 2 MB
              value={value.passportCopy}
              onChange={updatePassport}
              placeholder="Upload a scanned copy of your Passport (Front & Back)"
              required
              error={getFieldError('passport copy')}
            />
          </FormField>
        </Col>
      </Row>

      {/* Australian Residence */}
      <Row>
        <Col span={4}>
          <FormField label="In Australia at time of application?"
          required
          error={getFieldError('in australia at application')}>
            <Select
              value={value.inAustraliaAtApplication}
              onChange={(e) => updateField('inAustraliaAtApplication', e.target.value)}
              required
              error={getFieldError('in australia at application')}
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </Select>
          </FormField>
        </Col>
        <Col span={4}>
          <FormField label="Year of Arrival in Australia">
            <Input
              type="number"
              value={value.yearOfArrival}
              onChange={(e) => updateField('yearOfArrival', e.target.value)}
              placeholder="YYYY"
              min="1900"
              max={new Date().getFullYear()}
            />
          </FormField>
        </Col>
        <Col span={4}>
          <FormField label="Marital Status">
            <Select
              value={value.maritalStatus}
              onChange={(e) => updateField('maritalStatus', e.target.value)}
            >
              <option value="">Select</option>
              <option value="Single">Single</option>
              <option value="Married">Married</option>
              <option value="Widowed">Widowed</option>
              <option value="Divorced">Divorced</option>
              <option value="De facto">De facto</option>
            </Select>
          </FormField>
        </Col>
      </Row>

      {/* Contact Address */}
      <Row>
        <Col span={6}>
          <FormField label="Address"
          required
          error={getFieldError('address')}>
            <Input
              value={value.address}
              onChange={(e) => updateField('address', e.target.value)}
              placeholder="Street address"
              required
              error={getFieldError('address')}
            />
          </FormField>
        </Col>
        <Col span={3}>
          <FormField label="Suburb"
          required
          error={getFieldError('suburb')}>
            <Input
              value={value.suburb}
              onChange={(e) => updateField('suburb', e.target.value)}
              placeholder="Suburb/City"
              required
              error={getFieldError('suburb')}
            />
          </FormField>
        </Col>
        <Col span={3}>
          <FormField label="State/Territory"
          required
          error={getFieldError('state')}>
            {/* Main dropdown for Australian states */}
            <Select
              value={value.state}
              onChange={(e) => updateField('state', e.target.value)}
              required
            >
              <option value="">Select</option>
              <option value="ACT">ACT</option>
              <option value="NSW">NSW</option>
              <option value="NT">NT</option>
              <option value="QLD">QLD</option>
              <option value="SA">SA</option>
              <option value="TAS">TAS</option>
              <option value="VIC">VIC</option>
              <option value="WA">WA</option>
              <option value="OTHER">Other (type manually)</option>
            </Select>

            {/* Show manual input if "OTHER" is selected */}
            {value.state === 'OTHER' && (
              <div style={{ marginTop: '8px' }}>
                <Input
                  value={value.otherState || ''}
                  onChange={(e) => updateField('otherState', e.target.value)}
                  placeholder="Enter your state or territory"
                  required
                />
              </div>
            )}
          </FormField>
        </Col>
      </Row>

      <Row>
        <Col span={3}>
          <FormField label="Country"
          required
          error={getFieldError('country')}>
            <Select
              value={value.country || ''}
              onChange={(e) => updateField('country', e.target.value)}
              required
              error={getFieldError('country')}
            >
              <option value="">Select a country</option>
              {countryOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
          </FormField>
        </Col>
        <Col span={3}>
          <FormField label="Postcode"
          required
          error={getFieldError('postcode')}>
            <Input
              value={value.postcode}
              onChange={(e) => updateField('postcode', e.target.value)}
              placeholder="Postcode"
              required
              error={getFieldError('postcode')}
            />
          </FormField>
        </Col>
        
        <Col span={6}>
          <FormField 
            label="Permanent home address in home country (Only if different)"
          >
            <Input
              value={value.permanentHomeAddress}
              onChange={(e) => updateField('permanentHomeAddress', e.target.value)}
              placeholder="Complete address in home country"
            />
          </FormField>
        </Col>
      </Row>
      
      <Row>
        <Col span={4}>
          <FormField label="Emergency Contact Name"
          required
          error={getFieldError('emergencyContactName')}>
            <Input
              type="text"
              value={value.emergencyContactName}
              onChange={(e) => updateField('emergencyContactName', e.target.value)}
              placeholder="Full name of emergency contact"
              required
              error={getFieldError('emergencyContactName')}
            />
          </FormField>
        </Col>
        <Col span={4}>
          <FormField label="Emergency Contact Relation"
          required          error={getFieldError('emergencyContactRelation')}>
            <Input
              type="text"
              value={value.emergencyContactRelation}
              onChange={(e) => updateField('emergencyContactRelation', e.target.value)}
              placeholder="e.g., Parent, Friend, Sibling"
              required
              error={getFieldError('emergencyContactRelation')}
            />
          </FormField>
        </Col>
        
        <Col span={4}>
          <FormField label="Emergency Contact Telephone" 
          required
          error={getFieldError('emergencyContactNumber')}>
            <Input
              type="tel"
              value={value.emergencyContactNumber}
              onChange={(e) => updateField('emergencyContactNumber', e.target.value)}
              placeholder="Home/work phone"
              required
              error={getFieldError('emergencyContactNumber')}
            />
          </FormField>
        </Col>
      </Row>

      

      {/* Authorised Agent Section */}
      <SectionTitle>Authorised Agent (if applicable)</SectionTitle>
      
      <Row>
        <Col span={12}>
          <FormField 
            label="Agency Name"
            hint="Leave blank if not using an agent"
          >
            <Select
              value={value.agencyName}
              onChange={(e) => updateField('agencyName', e.target.value)}
            >
              <option value="">Select Agent</option>
              <option value="other">Other - Not Present in the list below.</option>
              {agentList.map((agent) => (
                  <option key={agent.agentName} value={agent.agentName}>
                      {agent.agentName}
                  </option>
              ))}
            </Select>
          </FormField>
          {/* Show custom agent input if "other" is selected or customAgent already has a value */}
            {(value.agencyName === "other") && (
            <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                <label
                style={{
                    display: "block",
                    marginBottom: "0.75rem",
                    fontWeight: "600",
                }}
                >
                Other Agent Name
                </label>
                <input
                type="text"
                name="customAgentName"
                value={value.customAgentName}
                onChange={(e) => updateField('customAgentName', e.target.value)}
                className="form-input"
                placeholder="Enter agent name"
                required
                />
            </div>
            )}
        </Col>
      </Row>
      {/* Info Section for New Agents */}
      <div style={{
        padding: 16,
        background: '#eff6ff',
        borderRadius: 8,
        border: '1px solid #93c5fd'
      }}>
        <h4 style={{ margin: '0 0 12px', color: '#192b64' }}>Are you a new Agent?</h4>
        <ul style={{ margin: 0, paddingLeft: 20, color: '#192b64', fontSize: 14 }}>
          <li style={{ marginBottom: 8 }}>If your agency is not listed above, you will need to apply for partnership.</li>
          <li>
            Submit your application here:{" "}
            <a 
              href="https://app.ciet.edu.au/agent-application" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ color: '#056ec4', textDecoration: 'underline' }}
            >
              EDUCATION AGENT APPLICATION
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
}